import { getCurrentInstance } from 'vue';
import { authGet } from '@/utilities/api';

interface CopyImageProps {
  /**
   * The st-image node imgsrc attribute.
   */
  nodeSrc: string;
}

async function canvasCopy(source: string) {
  const anonymousImage = new Image();
  anonymousImage.setAttribute('crossorigin', 'Anonymous');
  anonymousImage.setAttribute('referrerpolicy', 'no-referrer');
  anonymousImage.onload = function () {
    const canvas = document.createElement('canvas');
    canvas.width = anonymousImage.width;
    canvas.height = anonymousImage.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(anonymousImage, 0, 0);

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        } catch (err) {
          console.warn('Error in copying image: ', err);
        }
      }
    }, 'image/png');
  };
  anonymousImage.src = `${source}`;
}

export default function useCopyImage() {
  const _this = getCurrentInstance()?.proxy;
  if (!_this) return;

  /**
   * This function is specific to how the file API
   * is structured. This converts files from
   * the api URI to the files URI.
   */
  async function extractImageData(apiSource: string) {
    const baseRoute = _this!.$store.getters.getRoutes.files;
    const teamId = _this!.$store.getters.getTeamID;
    const id = /([\w-]+)\/?$/.exec(apiSource)?.[1];
    if (!baseRoute || !teamId || !id) {
      return;
    }
    const fileInfo = await authGet(`${baseRoute}/v1/${teamId}/files/${id}?noredirect=true`);
    return fileInfo?.data?.file as { url: string; type: string };
  }

  async function copyImage(image: HTMLImageElement | string, { nodeSrc = '' }: CopyImageProps) {
    const imgSrc = typeof image === 'string' ? image : image?.src;
    if (!imgSrc) return;
    if (nodeSrc.startsWith('https://images.unsplash.com')) {
      canvasCopy(nodeSrc);
    } else {
      const src = await extractImageData(imgSrc);
      if (src) canvasCopy(src.url);
    }
  }

  return {
    copyImage,
    extractImageData,
    canvasCopy,
  };
}
